import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import {
  Divider,
  FormControlLabel,
  Menu,
  MenuItem,
  Switch,
} from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import React, { ChangeEvent, useContext } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AdminInfoContext } from "../../context/AdminInfoContext";
import { useAlert } from "../../context/AlertContext";
import { AuthContext } from "../../context/AuthContext";
import { isAdmin } from "../../helpers/authHelpers";
import PermissionMenuItem from "./PermissionMenuItem";
import { useTheme } from "@mui/material/styles";
import logo from "../../images/LogoMenuBar.png";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { FeatureFlagName } from "../../types/feature-flags/featureFlag";
import { HideBehindFeature } from "../common/FeatureFlags/HideBehindFeature";

const getCustomerScopedPath = (
  customerIdFromPath?: string,
  customerIdFromUser?: string
) => {
  if (customerIdFromPath) {
    return `/customers/${customerIdFromPath}`;
  }
  return `/customers/${customerIdFromUser}`;
};

export default function MenuBar() {
  const { t } = useTranslation(["common", "menuBar"]);
  const { customerId: customerIdFromPath } = useParams();
  const { authenticated, user } = useContext(AuthContext);
  const customerIdFromUser = user?.customerId;
  const customerScopedPath = getCustomerScopedPath(
    customerIdFromPath,
    customerIdFromUser
  );

  const theme = useTheme();

  function homepage() {
    return authenticated ? customerScopedPath : "/login";
  }

  return (
    <Box>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "white",
        }}
      >
        <Toolbar>
          <Box>
            <Link to={homepage()}>
              <img
                src={logo}
                alt="logo"
                loading="lazy"
                style={{
                  maxHeight: 45,
                  maxWidth: 45,
                  width: "auto",
                  height: "auto",
                }}
              />
            </Link>
          </Box>
          <Typography
            className="hidden sm:inline"
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              paddingLeft: 1,
            }}
          >
            <Link
              to={homepage()}
              style={{
                color: theme.palette.primary.main,
                textDecoration: "none",
              }}
            >
              Continuity Replay
            </Link>
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Box
            className="flex"
            sx={{
              color: theme.palette.primary.main,
            }}
          >
            <PermissionMenuItem
              path={`${customerScopedPath}/dashboard`}
              user={user}
              resource="interactions"
              requiredPermission="Read"
            >
              {t("menuBar:dashboard")}
            </PermissionMenuItem>
            <PermissionMenuItem
              path={`${customerScopedPath}/search`}
              user={user}
              resource="interactions"
              requiredPermission="Read"
            >
              {t("common:search")}
            </PermissionMenuItem>
            <Divider orientation="vertical" flexItem />
            <AccountIcon customerScopedPath={customerScopedPath} />
          </Box>
        </Toolbar>
      </AppBar>
      <Toolbar />
    </Box>
  );
}

export interface AccountIconProps {
  customerScopedPath: string;
}

const AccountIcon = ({ customerScopedPath }: AccountIconProps) => {
  const { t } = useTranslation(["common", "menuBar", "auth"]);
  const { isVisible: isAdminInfoVisible, setVisible: setAdminInfoVisible } =
    useContext(AdminInfoContext);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const showAlert = useAlert();
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const { authenticated, user, logout } = useContext(AuthContext);
  const theme = useTheme();

  const handleResetPassword = () => {
    navigateTo("/reset-password");
  };

  const handleLogout = async () => {
    try {
      handleClose();
      await logout();
    } catch (error: unknown) {
      if (!(error instanceof Error)) return;
      showAlert(error.message, "error");
    }
  };

  const navigate = useNavigate();

  const navigateTo = (path: string) => {
    navigate(path);
    handleClose();
  };

  const handleInfoBarToggle = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setAdminInfoVisible(checked);
  };

  return authenticated ? (
    <div>
      <IconButton
        size="large"
        edge="end"
        sx={{
          color: theme.palette.primary.main,
        }}
        aria-label="menu"
        onClick={handleClick}
      >
        <AccountCircleIcon />
      </IconButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <PermissionMenuItem
          path={`${customerScopedPath}/sites`}
          close={handleClose}
          user={user}
          resource="sites"
          requiredPermission="Read"
        >
          {t("common:sites")}
        </PermissionMenuItem>
        <PermissionMenuItem
          path={`${customerScopedPath}/users`}
          close={handleClose}
          user={user}
          resource="users"
          requiredPermission="Read"
        >
          {t("menuBar:users")}
        </PermissionMenuItem>
        <PermissionMenuItem
          path={`${customerScopedPath}/roles`}
          close={handleClose}
          user={user}
          resource="roles"
          requiredPermission="Read"
        >
          {t("menuBar:roles")}
        </PermissionMenuItem>
        <Divider />
        <PermissionMenuItem
          path={`${customerScopedPath}/connectors`}
          close={handleClose}
          user={user}
          resource="connectors"
          requiredPermission="Read"
        >
          {t("menuBar:connectors")}
        </PermissionMenuItem>
        <PermissionMenuItem
          path={`${customerScopedPath}/audit-logs`}
          close={handleClose}
          user={user}
          resource="auditlogs"
          requiredPermission="Read"
        >
          {t("menuBar:auditLogs")}
        </PermissionMenuItem>
        <HideBehindFeature name={FeatureFlagName.BulkExport}>
          <PermissionMenuItem
            path={`${customerScopedPath}/jobs/bulk-export`}
            close={handleClose}
            user={user}
            resource="interactions"
            requiredPermission="Export"
          >
            {t("common:bulkExport")}
          </PermissionMenuItem>
        </HideBehindFeature>
        <PermissionMenuItem
          path={`${customerScopedPath}/views`}
          close={handleClose}
          user={user}
          resource="views"
          requiredPermission="*"
        >
          {t("menuBar:manageViews")}
        </PermissionMenuItem>
        <Divider />
        <PermissionMenuItem
          path={`${customerScopedPath}/customization/set-logo`}
          close={handleClose}
          user={user}
          resource="customization"
          requiredPermission="Update"
        >
          {t("menuBar:setLogo")}
        </PermissionMenuItem>
        <PermissionMenuItem
          path={`${customerScopedPath}/header-names`}
          close={handleClose}
          user={user}
          resource="customers"
          requiredPermission="Update"
        >
          {t("menuBar:configureTableHeaders")}
        </PermissionMenuItem>
        <Divider />
        {isAdmin(user) && (
          <div>
            <PermissionMenuItem
              path="/federated-auth"
              close={handleClose}
              user={user}
              resource="customization"
              requiredPermission="Read"
            >
              {t("menuBar:federatedAuth")}
            </PermissionMenuItem>
            <MenuItem onClick={() => navigateTo("/customers/admin")}>
              {t("menuBar:addChangeCustomer")}
            </MenuItem>
            <MenuItem onClick={() => navigateTo("/customers/feature-flags")}>
              {t("menuBar:manageFeatureFlags")}
            </MenuItem>
            <MenuItem>
              <FormControlLabel
                control={
                  <Switch
                    checked={isAdminInfoVisible}
                    onChange={handleInfoBarToggle}
                  />
                }
                label={t("menuBar:showInfoBar")}
              />
            </MenuItem>

            <Divider />
          </div>
        )}
        {/* Generate a switch in menuitem that switches between English and French Canadian */}
        <MenuItem>
          <FormControlLabel
            control={
              <Switch
                checked={i18n.language === "fr-CA"}
                onChange={() => {
                  const newLang = i18n.language === "fr-CA" ? "en" : "fr-CA";
                  i18n.changeLanguage(newLang);
                }}
              />
            }
            label={t("common:language")}
          />
        </MenuItem>
        <MenuItem onClick={handleResetPassword}>
          {t("menuBar:resetPassword")}
        </MenuItem>
        <MenuItem onClick={handleLogout}>{t("auth:logout")}</MenuItem>
      </Menu>
    </div>
  ) : null;
};
